import { environment } from '../../../environments/environment';

/**
 * Where a whitelabel (chatbot builder) call goes. prism-new serves the old gateway's
 * `/api/v1/whitelabel/<path>` natively at `/api/v1/bot-builder/<path>` on the signed-in user's
 * session, keeping the gateway's sub-paths except schedules and delivery config, which moved to
 * `/chatbots/:chatbotId/...`. Every whitelabel call goes to prism-new while prism-new routing is
 * on, and to the gateway when it is off; MicroServiceHttpService makes that call.
 */

const BOT_BUILDER_PREFIX = 'bot-builder';

/** Old gateway sub-path → prism-new sub-path, for the routes prism-new mounts elsewhere. */
const RENAMES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^chatbot\/bot\/([^/?]+)\/dag-executions\//, 'chatbots/$1/schedules/dag-executions/'],
  [/^chatbot\/bot\/([^/?]+)\/(schedules|delivery-config)(?=[/?]|$)/, 'chatbots/$1/$2'],
];

/** The prism-new path (under the API root, query kept) for an old whitelabel gateway path. */
export function botBuilderPath(path: string): string {
  let mapped = path.replace(/^\/+/, '');
  for (const [pattern, replacement] of RENAMES) {
    mapped = mapped.replace(pattern, replacement);
  }
  return `${BOT_BUILDER_PREFIX}/${mapped}`;
}

const CALL_AUDITOR_PREFIX = 'call-auditor';

/**
 * The prism-new path for an old call-auditor gateway path. prism-new serves the whole gateway
 * surface at `/api/v1/call-auditor/<path>`, sub-paths unchanged.
 */
export function callAuditorPath(path: string): string {
  return `${CALL_AUDITOR_PREFIX}/${path.replace(/^\/+/, '')}`;
}

/** The URL on prism-new for a bot-builder route, for a call made with fetch (SSE, keepalive, uploads). */
export function prismNewUrl(route: string): string {
  const root = environment.prismNew.apiUrl.replace(/\/+$/, '');
  return `${root}/${route.replace(/^\/+/, '')}`;
}

/** The URL on the whitelabel gateway for an old whitelabel path, for a call made with fetch. */
export function gatewayUrl(path: string): string {
  const base = environment.microAppService_url.whitelabel.url.replace(/\/+$/, '');
  return `${base}/${path.replace(/^\/+/, '')}`;
}

/** True when `url` points at prism-new, i.e. it needs the prism session and speaks its envelope. */
export function isPrismNewUrl(url: string): boolean {
  const root = environment.prismNew.apiUrl.replace(/\/+$/, '');
  return environment.prismNew.enabled && (url === root || url.startsWith(`${root}/`));
}
